<template>
  <section class="videoCoursePage">
    <div class="videoCoursePage__filters">
      <div class="videoCoursePage__field">
        <span class="videoCoursePage__label">{{ t('videoCourseClass') }}</span>
        <select
          v-model="selectedCourse"
          class="videoCoursePage__select without-outline"
          @change="handleCourseChange"
        >
          <option :value="null">{{ t('videoCourseAllClasses') }}</option>
          <option
            v-for="item in courseOptions"
            :key="`course-${item.value}`"
            :value="item.value"
          >
            {{ item.text }}
          </option>
        </select>
      </div>

      <div class="videoCoursePage__field">
        <span class="videoCoursePage__label">{{ t('videoCourseSubject') }}</span>
        <select
          v-model="selectedSubject"
          class="videoCoursePage__select without-outline"
          :disabled="!selectedCourse || isSubjectsLoading"
          @change="handleSubjectChange"
        >
          <option :value="null">{{ t('videoCourseAllSubjects') }}</option>
          <option
            v-for="item in subjectOptions"
            :key="`subject-${item.value}`"
            :value="item.value"
          >
            {{ item.text }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="!isVideosLoading && hasFetchedVideos && !videoList.length" class="videoCoursePage__empty">
      <div class="videoCoursePage__alert">
        {{ emptyMessage }}
      </div>
    </div>

    <div v-if="videoList.length" class="videoCoursePage__accordionList">
      <div
        v-for="item in videoList"
        :key="item.id"
        class="videoCoursePage__accordionItem"
      >
        <button
          type="button"
          class="videoCoursePage__accordionHead without-outline"
          @click="toggleVideo(item)"
        >
          <div class="videoCoursePage__accordionMain">
            <p class="videoCoursePage__accordionTitle">{{ item.title }}</p>
            <p v-if="item.meta" class="videoCoursePage__accordionMeta">{{ item.meta }}</p>
          </div>
          <span class="videoCoursePage__accordionIcon material-icons">
            {{ openedVideoId === item.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </span>
        </button>

        <div v-if="openedVideoId === item.id" class="videoCoursePage__accordionBody">
          <iframe
            v-if="item.embedUrl"
            class="videoCoursePage__iframe"
            :src="item.embedUrl"
            frameborder="0"
            referrerpolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          >
          </iframe>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useVideoCourseCoursesQuery,
  useVideoCourseSubjectsQuery,
  useVideoCourseVideosQuery
} from '@/queries';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';

const { t } = useI18n();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();

const selectedCourse = ref(null);
const selectedSubject = ref(null);
const openedVideoId = ref(null);

const courseFallback = computed(() => t('videoCourseClassFallback'));
const subjectFallback = computed(() => t('videoCourseSubjectFallback'));
const lessonFallback = computed(() => t('videoCourseLessonFallback'));

const coursesQuery = useVideoCourseCoursesQuery(courseFallback, {
  retry: false
});

const subjectsQuery = useVideoCourseSubjectsQuery(selectedCourse, subjectFallback, {
  retry: false,
  placeholderData: (previousData) => previousData
});

const videoFilters = computed(() => ({
  course_id: selectedCourse.value,
  subject_id: selectedSubject.value
}));

const videosQuery = useVideoCourseVideosQuery(videoFilters, lessonFallback, {
  retry: false,
  placeholderData: (previousData) => previousData
});

const courseOptions = computed(() =>
  Array.isArray(coursesQuery.data.value) ? coursesQuery.data.value : []
);

const subjectOptions = computed(() =>
  Array.isArray(subjectsQuery.data.value) ? subjectsQuery.data.value : []
);

const videoList = computed(() =>
  Array.isArray(videosQuery.data.value) ? videosQuery.data.value : []
);

const isSubjectsLoading = computed(() => Boolean(subjectsQuery.isPending.value || subjectsQuery.isFetching.value));
const isVideosLoading = computed(() => Boolean(videosQuery.isPending.value || videosQuery.isFetching.value));
const pageLoading = computed(() => Boolean(coursesQuery.isPending.value || videosQuery.isPending.value));
const hasFetchedVideos = computed(() => !videosQuery.isPending.value && (videosQuery.isFetched?.value ?? true));

const emptyMessage = computed(() => {
  if (selectedCourse.value || selectedSubject.value) {
    return t('videoCourseFilteredEmpty');
  }

  return t('noData');
});

function handleCourseChange() {
  selectedSubject.value = null;
  openedVideoId.value = null;
}

function handleSubjectChange() {
  openedVideoId.value = null;
}

function toggleVideo(item) {
  openedVideoId.value = openedVideoId.value === item.id ? null : item.id;
}

watch(
  () => pageLoading.value,
  (value) => {
    loadingStore.set('video-course-page', value);
  },
  { immediate: true }
);

watch(
  () => [selectedCourse.value, selectedSubject.value],
  () => {
    openedVideoId.value = null;
  }
);

onMounted(() => {
  topbarStore.setTitle(t('video-course'));
});

onBeforeUnmount(() => {
  loadingStore.hide('video-course-page');
});
</script>

<style scoped lang="scss">
.videoCoursePage {
  padding: 24px;

  @media screen and (max-width: 768px) {
    padding: 16px;
  }

  &__filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 24px;

    @media screen and (max-width: 768px) {
      grid-template-columns: 1fr;
      margin-bottom: 16px;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    color: #474a57;
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  &__select {
    width: 100%;
    min-height: 44px;
    border: 1px solid #ebecee;
    border-radius: 8px;
    background-color: #ffffff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M3.5 5.25L7 8.75L10.5 5.25' stroke='%238B8B8D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 14px 14px;
    color: #18191f;
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    line-height: 20px;
    padding: 0 42px 0 14px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    box-shadow: none;
    outline: none;
    cursor: pointer;

    &::-ms-expand {
      display: none;
    }

    &:disabled {
      background-color: #f5f6f7;
      color: #8b8b8d;
      cursor: default;
    }
  }

  &__accordionList {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__accordionItem {
    background: #ffffff;
    border: 1px solid #ebecee;
    border-radius: 12px;
    box-shadow: 0 2px 6px 0 #2f53971a;
    overflow: hidden;
  }

  &__accordionHead {
    align-items: center;
    background: #ffffff;
    border: 0;
    cursor: pointer;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 16px;
    text-align: left;
    width: 100%;
  }

  &__accordionMain {
    min-width: 0;
  }

  &__accordionTitle {
    color: #18191f;
    font-family: "Roboto", sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    margin: 0;
    word-break: break-word;
  }

  &__accordionMeta {
    color: #737982;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    line-height: 18px;
    margin: 4px 0 0;
  }

  &__accordionIcon {
    color: #003399;
    flex-shrink: 0;
    font-size: 24px;
  }

  &__accordionBody {
    padding: 0 16px 16px;
  }

  &__iframe {
    aspect-ratio: 16 / 9;
    display: block;
    width: 100%;
    min-height: 260px;

    @media screen and (max-width: 768px) {
      min-height: 220px;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
  }

  &__alert {
    width: 100%;
    border: 1px solid #fbd99f;
    background: #fff8e8;
    color: #fbad2f;
    border-radius: 4px;
    padding: 14px 12px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }
}
</style>
